import { call, put, take } from 'redux-saga/effects';
import * as recordActions from '../actions/recordActionsHelper';
import * as questionActions from '../actions/questionActionsHelper';
import * as api from '../../api';

export function* handleGetRecords() {
    try {
        const response = yield call(api.getRecords);
        yield put(recordActions.getRecord.success(response.data));
    } catch (error) {
        yield put(recordActions.getRecord.failed(error.message));
    }
}

export function* handleCreateNewRecord(action) {
    try {
        const response = yield call(api.createNewRecord, action.payload);
        yield put(recordActions.createNewRecord.success(response.data));
    } catch (error) {
        yield put(recordActions.createNewRecord.failed(error.message));
    }
}

export function* handleUpdateRecord(action) {
    try {
        const { id, data } = action.payload;
        const response = yield call(api.updateRecord, id, data);
        yield put(recordActions.updateRecord.success(response.data));
    } catch (error) {
        yield put(recordActions.updateRecord.failed(error.message));
    }
}

export function* handleDeleteRecord(action) {
    try {
        const response = yield call(api.deleteRecord, action.payload);
        yield put(recordActions.deleteRecord.success(response.data));
    } catch (error) {
        yield put(recordActions.deleteRecord.failed(error.message));
    }
}

// Session Management Sagas

export function* handleCheckActiveRecord(action) {
    try {
        const { userId, section } = action.payload;
        const response = yield call(api.checkActiveRecord, userId, section);
        yield put(recordActions.checkActiveRecord.success(response.data));
    } catch (error) {
        yield put(recordActions.checkActiveRecord.failed(error.message));
    }
}

export function* handlePauseRecord(action) {
    try {
        const { recordId } = action.payload;
        const response = yield call(api.pauseRecord, recordId);
        yield put(recordActions.pauseRecord.success(response.data));
    } catch (error) {
        yield put(recordActions.pauseRecord.failed(error.message));
    }
}

export function* handleResumeRecord(action) {
    try {
        const { recordId } = action.payload;
        const response = yield call(api.resumeRecord, recordId);
        yield put(recordActions.resumeRecord.success(response.data));
    } catch (error) {
        yield put(recordActions.resumeRecord.failed(error.message));
    }
}

export function* handleEndRecord(action) {
    try {
        const { recordId } = action.payload;
        const response = yield call(api.endRecord, recordId);
        yield put(recordActions.endRecord.success(response.data));
    } catch (error) {
        yield put(recordActions.endRecord.failed(error.message));
    }
}

// Saga for getting user section statistics
export function* handleGetUserSectionStats(action) {
    try {
        const { userId } = action.payload;
        const response = yield call(api.getUserSectionStats, userId);
        yield put(recordActions.getUserSectionStats.success(response.data));
    } catch (error) {
        yield put(recordActions.getUserSectionStats.failed(error.message));
    }
}

// Saga for getting monthly subject accuracy
export function* handleGetMonthlySubjectAccuracy(action) {
    try {
        const { userId } = action.payload;
        const response = yield call(api.getMonthlySubjectAccuracy, userId);
        yield put(recordActions.getMonthlySubjectAccuracy.success(response.data));
    } catch (error) {
        yield put(recordActions.getMonthlySubjectAccuracy.failed(error.message));
    }
}

// Saga for getting daily accuracy trend
export function* handleGetDailyAccuracyTrend(action) {
    try {
        const { userId, subject } = action.payload;
        const response = yield call(api.getDailyAccuracyTrend, userId, subject);
        yield put(recordActions.getDailyAccuracyTrend.success(response.data));
    } catch (error) {
        yield put(recordActions.getDailyAccuracyTrend.failed(error.message));
    }
}

// Combined saga: Update record then get next question
export function* handleUpdateRecordAndGetNextQuestion(action) {
    try {
        const { recordUpdate, questionRequest } = action.payload;

        console.log('Starting combined update record and get next question');

        // Step 1: Update the record first
        const updateResponse = yield call(api.updateRecord, recordUpdate.id, recordUpdate.data);
        yield put(recordActions.updateRecord.success(updateResponse.data));

        console.log('Record updated successfully, now getting next question');

        // Step 2: After successful record update, get next question
        // Dispatch and wait for the individual saga to complete
        yield put(questionActions.getQuestionForPractice.request({
            ...questionRequest,
            recordId: recordUpdate.id,
        }));

        // Wait for either success or failure from the question saga
        const questionResult = yield take([
            questionActions.getQuestionForPractice.success,
            questionActions.getQuestionForPractice.failed
        ]);

        // If it failed, throw error to trigger outer catch
        if (questionResult.type === questionActions.getQuestionForPractice.failed.type) {
            throw new Error(questionResult.payload);
        }

        // Signal that the combined operation completed
        yield put(recordActions.updateRecordAndGetNextQuestion.success({
            updatedRecord: updateResponse.data,
            nextQuestion: questionResult.payload
        }));

    } catch (error) {
        console.error('Combined operation failed:', error);
        yield put(recordActions.updateRecordAndGetNextQuestion.failed(error.message));
    }
}

// Combined saga: Create new record then get first question  
export function* handleCreateRecordAndGetFirstQuestion(action) {
    try {
        const { recordData, questionRequest } = action.payload;

        // Step 1: Create the new record first
        const createResponse = yield call(api.createNewRecord, recordData);
        yield put(recordActions.createNewRecord.success(createResponse.data));

        // Step 2: After successful record creation, get first question
        // Dispatch and wait for the individual saga to complete
        yield put(questionActions.getQuestionForPractice.request({
            ...questionRequest,
            recordId: createResponse.data?._id,
        }));

        // Wait for either success or failure from the question saga
        const questionResult = yield take([
            questionActions.getQuestionForPractice.success,
            questionActions.getQuestionForPractice.failed
        ]);

        // If it failed, throw error to trigger outer catch
        if (questionResult.type === questionActions.getQuestionForPractice.failed.type) {
            throw new Error(questionResult.payload);
        }

        // Signal that the combined operation completed
        yield put(recordActions.createRecordAndGetFirstQuestion.success({
            newRecord: createResponse.data,
            firstQuestion: questionResult.payload
        }));

    } catch (error) {
        console.error('Combined create and get first question failed:', error);
        yield put(recordActions.createRecordAndGetFirstQuestion.failed(error.message));
    }
}

// Combined saga: Resume record then get current question
export function* handleResumeRecordAndGetQuestion(action) {
    try {
        const { recordId, questionRequest } = action.payload;

        console.log('Starting combined resume record and get question');

        // Step 1: Resume the record first
        const resumeResponse = yield call(api.resumeRecord, recordId);
        yield put(recordActions.resumeRecord.success(resumeResponse.data));

        console.log('Record resumed successfully, now getting current question');

        // Step 2: After successful resume, get current question
        // Dispatch and wait for the individual saga to complete
        yield put(questionActions.getQuestionForPractice.request({
            ...questionRequest,
            recordId,
        }));

        // Wait for either success or failure from the question saga
        const questionResult = yield take([
            questionActions.getQuestionForPractice.success,
            questionActions.getQuestionForPractice.failed
        ]);

        // If it failed, throw error to trigger outer catch
        if (questionResult.type === questionActions.getQuestionForPractice.failed.type) {
            throw new Error(questionResult.payload);
        }

        // Signal that the combined operation completed
        yield put(recordActions.resumeRecordAndGetQuestion.success({
            resumedRecord: resumeResponse.data,
            currentQuestion: questionResult.payload
        }));

    } catch (error) {
        console.error('Combined resume and get question failed:', error);
        yield put(recordActions.resumeRecordAndGetQuestion.failed(error.message));
    }
}
